@if (isLoading) {
  <div class="loading">Loading profile…</div>
} @else if (user) {
  <div class="page-head">
    <div>
      <h1>{{ user.firstName }} {{ user.lastName }}</h1>
      <div class="sub">{{ user.email }}</div>
    </div>
  </div>

  <div class="page-body">
    <!-- View-only until Edit: only one admin can change a user at a time. -->
    <app-edit-lock-bar
      class="edit-lock"
      entityType="user"
      noun="user"
      [entityId]="user.userId"
      (editingChange)="onEditModeChange($event)">
    </app-edit-lock-bar>

    <div class="panel">
      <div class="panel-head">
        <div class="panel-title" style="margin:0">Account</div>
        @if (!isEditing && canEditProfile) {
          <fieldset class="edit-scope inline" [disabled]="!editMode">
            <button type="button" class="act-ghost act-sm" (click)="startEdit()">Edit</button>
          </fieldset>
        }
      </div>

      @if (editMessage && !isEditing) {
        <div class="note note-ok">{{ editMessage }}</div>
      }

      @if (!isEditing) {
        <dl class="facts">
          <div>
            <dt>User ID</dt>
            <dd class="mono">{{ user.userId }}</dd>
          </div>
          <div>
            <dt>Name</dt>
            <dd>{{ user.firstName }} {{ user.lastName }}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{{ user.email }}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{{ user.phoneNumber || '—' }}</dd>
          </div>
          <div>
            <dt>Sign-in</dt>
            <dd>
              @if (user.isDisabled) {
                <span class="pill pill-red">Disabled</span>
                @if (user.disabledAtUtc) { <small class="hint">since {{ user.disabledAtUtc | date: 'd MMM y, HH:mm' }}</small> }
                @if (user.disabledReason) { <small class="hint">Reason: {{ user.disabledReason }}</small> }
                <small class="hint">Enable it from the user's page in edit mode.</small>
              } @else {
                <span class="pill pill-green">Active</span>
              }
            </dd>
          </div>
        </dl>
      } @else {
        @if (editError) { <div class="note note-err">{{ editError }}</div> }

        <ng-container *staffCan="'Users.EditProfile'"><form [formGroup]="editForm" (ngSubmit)="saveProfile()">
          <div class="form-grid">
            <label class="field">
              <span>First name *</span>
              <input class="inp" type="text" formControlName="firstName" maxlength="100" />
              @if (invalid('firstName')) { <small class="err">First name is required.</small> }
            </label>
            <label class="field">
              <span>Last name *</span>
              <input class="inp" type="text" formControlName="lastName" maxlength="100" />
              @if (invalid('lastName')) { <small class="err">Last name is required.</small> }
            </label>
            <label class="field">
              <span>Phone</span>
              <input class="inp" type="text" formControlName="phoneNumber" maxlength="30" />
            </label>
            <label class="field">
              <span>Email</span>
              <input class="inp" type="text" [value]="user.email" disabled />
              <small class="hint">Email and username are managed by the identity server.</small>
            </label>
          </div>

          <div class="form-actions">
            <button type="button" class="act-ghost" (click)="cancelEdit()" [disabled]="isSaving">Cancel</button>
            <button type="submit" class="act" [disabled]="isSaving">
              {{ isSaving ? 'Saving…' : 'Save changes' }}
            </button>
          </div>
        </form></ng-container>
      }
    </div>

    <!-- Who has acted on this account. SuperAdmin-only server-side, so Support following the
         link sees the log's own 403 rather than a broken page. -->
    @if (canSeeAuditTrail) {
      <div class="panel audit-link-panel">
        <div>
          <div class="panel-title" style="margin:0">Admin activity</div>
          <p class="hint">
            Credential resets, deletions and plan changes made on this account, with who made them.
          </p>
        </div>
        <a class="act-ghost act-sm"
           [routerLink]="['/security/admin-activity']"
           [queryParams]="{ targetUserId: userId }">View history</a>
      </div>
    }

    <!-- Sign-in password. Held hashed by the identity server, so it can be replaced but
         never displayed; a reset also clears any lockout from failed sign-ins. -->
    <div class="panel">
      <div class="panel-title">Password</div>

      @if (tempPassword) {
        <div class="note note-ok pin-reveal" role="status">
          <div>
            <div class="pin-value temp-password">{{ tempPassword }}</div>
            <small>
              Temporary password. Shown once — hand it to the user now, it cannot be read again.
              Ask them to change it after signing in.
            </small>
            @if (tempPasswordAccountDisabled) {
              <small class="temp-password-blocked">
                This account is disabled, so this password cannot sign in until the account is enabled.
              </small>
            }
          </div>
          <button type="button" class="act-ghost act-sm" (click)="dismissTempPassword()">Done</button>
        </div>
      }

      @if (passwordMessage) { <div class="note note-ok">{{ passwordMessage }}</div> }
      @if (passwordError) { <div class="note note-err">{{ passwordError }}</div> }

     <fieldset class="edit-scope" [disabled]="!editMode">
      @if (isSelf) {
        <p class="hint">Use your account settings to change your own password.</p>
      } @else if (!canResetPassword) {
        <p class="hint">Only a SuperAdmin can reset a platform staff password.</p>
      } @else if (confirmPasswordReset) {
        <div class="note note-err pin-confirm" role="alertdialog">
          <span>
            Replace the password for <strong>{{ user.email }}</strong>? Their current password
            stops working immediately. Sessions already signed in last until they expire.
          </span>
          <div class="pin-confirm-actions">
            <ng-container *staffCan="'Users.ResetCredentials'"><button type="button" class="act-danger act-sm" [disabled]="passwordResetting" (click)="confirmResetPassword()">
              {{ passwordResetting ? 'Resetting…' : 'Yes, reset it' }}
            </button></ng-container>
            <button type="button" class="act-link" [disabled]="passwordResetting" (click)="cancelPasswordReset()">Cancel</button>
          </div>
        </div>
      } @else {
        <div class="form-actions">
          <ng-container *staffCan="'Users.ResetCredentials'"><button type="button" class="act" [disabled]="passwordResetting" (click)="requestPasswordReset()">
            Reset password
          </button></ng-container>
        </div>
        <p class="hint">
          Issues a temporary password and shows it here once. The existing password cannot be
          displayed — only replaced.
        </p>
      }
     </fieldset>
    </div>

    <!-- Authenticator app. Platform staff must sign in with one; the identity server asks for it
         at every sign-in. A reset is for a lost phone: SuperAdmin-only, never your own account. -->
    <div class="panel">
      <div class="panel-title">Authenticator</div>

      @if (authenticatorMessage) { <div class="note note-ok" role="status">{{ authenticatorMessage }}</div> }
      @if (authenticatorWarning) { <div class="note note-warn" role="status">{{ authenticatorWarning }}</div> }
      @if (authenticatorError) { <div class="note note-err">{{ authenticatorError }}</div> }

      <dl class="facts">
        <div>
          <dt>Status</dt>
          <dd>
            @if (!targetRequiresAuthenticator) {
              <span class="pill pill-grey">Not required</span>
            } @else if (authenticatorOn) {
              <span class="pill pill-green">On</span>
            } @else {
              <span class="pill pill-amber">Not set up</span>
            }
          </dd>
        </div>
      </dl>

      @if (!targetRequiresAuthenticator) {
        <p class="hint">
          Only platform staff (SuperAdmin, Support, Application processor) sign in with an
          authenticator app. This account signs in with its password alone.
        </p>
      } @else {
        <p class="hint">
          Required for platform staff. Reset it only when they've lost their phone and their recovery codes.
          @if (!authenticatorOn) { They are asked to set it up at their next sign-in; nothing for an admin to do. }
        </p>

       <fieldset class="edit-scope" [disabled]="!editMode">
        @if (isSelf) {
          <p class="hint">Nobody can reset their own authenticator here. Use a recovery code, or ask another SuperAdmin.</p>
        } @else if (!canResetAuthenticator) {
          <p class="hint">Only a SuperAdmin can reset a platform staff authenticator.</p>
        } @else if (confirmAuthenticatorReset) {
          <div class="note note-err authenticator-confirm" role="alertdialog" aria-modal="true"
            aria-labelledby="authenticator-confirm-title">
            <strong id="authenticator-confirm-title">
              Reset {{ user.firstName }} {{ user.lastName }}'s authenticator?
            </strong>
            <ul>
              <li>Their current app and recovery codes stop working.</li>
              <li>They're signed out everywhere.</li>
              <li>At their next sign-in they set it up again, starting with a code sent to their email.</li>
            </ul>
            <label class="field authenticator-reason">
              <span>Reason (kept in admin activity)</span>
              <input class="inp" type="text" maxlength="500" [(ngModel)]="authenticatorReason"
                placeholder="e.g. Lost phone, confirmed by video call" />
            </label>
            <div class="pin-confirm-actions">
              <button type="button" class="act-danger act-sm" [disabled]="authenticatorResetting"
                (click)="confirmResetAuthenticator()">
                {{ authenticatorResetting ? 'Resetting…' : 'Reset authenticator' }}
              </button>
              <button type="button" class="act-link" [disabled]="authenticatorResetting"
                (click)="cancelAuthenticatorReset()">Cancel</button>
            </div>
            <small class="hint">Closes only with Cancel or Reset.</small>
          </div>
        } @else {
          <div class="form-actions">
            <button type="button" class="act-danger" [disabled]="authenticatorResetting"
              (click)="requestAuthenticatorReset()">
              Reset authenticator
            </button>
          </div>
          <p class="hint">
            SuperAdmins only · never on your own account. Ends every session and refresh token they
            hold; written to admin activity and the staff sign-in log.
          </p>
        }
       </fieldset>
      }
    </div>

    <!-- The staff sign-in log for this person. SuperAdmin-only server-side; staff accounts only. -->
    @if (showRecentSignIns) {
      <div class="panel">
        <div class="panel-head">
          <div class="panel-title" style="margin:0">Recent sign-ins</div>
          <a class="act-ghost act-sm"
             [routerLink]="['/security/staff-sign-ins']"
             [queryParams]="{ userId: userId }">All sign-ins</a>
        </div>

        @if (signInsLoading) {
          <div class="muted">Loading sign-ins…</div>
        } @else if (signInsError) {
          <div class="note note-err">{{ signInsError }}</div>
        } @else if (!recentSignIns.length) {
          <div class="muted">No sign-ins recorded yet.</div>
        } @else {
          <div class="tbl-wrap">
            <table class="tbl">
              <thead>
                <tr>
                  <th>Signed in</th>
                  <th>Ended</th>
                  <th>How it ended</th>
                  <th>With</th>
                  <th>App</th>
                </tr>
              </thead>
              <tbody>
                @for (session of recentSignIns; track session.sessionId) {
                  <tr>
                    <td>{{ session.signedInUtc | date: 'd MMM y, HH:mm' }}</td>
                    <td>
                      @if (session.endedUtc) {
                        {{ session.endedUtc | date: 'd MMM y, HH:mm' }}
                      } @else {
                        <span class="muted">still signed in</span>
                      }
                    </td>
                    <td><span class="pill" [ngClass]="signInEndedClass(session)">{{ signInEndedLabel(session) }}</span></td>
                    <td>{{ signInMethodLabel(session.method) }}</td>
                    <td>{{ signInAppLabel(session.app) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    }

    <!-- POS PIN: the credential that unlocks a till and clears manager approvals.
         Visible to any admin, resettable by SuperAdmin only. -->
    <div class="panel">
      <div class="panel-title">POS PIN</div>

      @if (pinLoading) {
        <div class="muted">Loading PIN status…</div>
      } @else if (!pinStatus) {
        <div class="note note-err">{{ pinError || 'PIN status unavailable.' }}</div>
      } @else if (pinStatus.blocked) {
        <div class="muted">{{ pinStatus.blocked }}</div>
      } @else {
        @if (newPin) {
          <div class="note note-ok pin-reveal" role="status">
            <div>
              <div class="pin-value">{{ newPin }}</div>
              <small>
                New PIN for {{ newPinScopeLabel }}. Shown once — hand it to the user now,
                it cannot be read again.
              </small>
            </div>
            <button type="button" class="act-ghost act-sm" (click)="dismissNewPin()">Done</button>
          </div>
        }

        @if (pinMessage) { <div class="note note-ok">{{ pinMessage }}</div> }
        @if (pinError) { <div class="note note-err">{{ pinError }}</div> }

        <!-- Only shown when there is a real choice: staff and single-business users have one PIN. -->
        @if (pinScopes.length > 1) {
          <label class="field pin-scope">
            <span>Which PIN</span>
            <select class="inp" [(ngModel)]="selectedPinScopeId">
              <option [ngValue]="null">Select…</option>
              @for (scope of pinScopes; track scope.businessId) {
                <option [ngValue]="scope.businessId">
                  {{ scope.label }}{{ scope.hasPin ? '' : ' — no PIN set' }}
                </option>
              }
            </select>
          </label>
        }

        @if (selectedPinScope) {
          <dl class="facts">
            <div>
              <dt>Scope</dt>
              <dd>{{ selectedPinScope.label }}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{{ selectedPinScope.hasPin ? 'PIN set' : 'No PIN set' }}</dd>
            </div>
            <div>
              <dt>Reach</dt>
              <dd>{{ scopeReach(selectedPinScope) }}</dd>
            </div>
          </dl>
        } @else {
          <div class="muted">Choose which PIN to work with.</div>
        }

       <fieldset class="edit-scope" [disabled]="!editMode">
        @if (!canResetPin) {
          <p class="hint">
            {{ pinStatus.isPlatformStaff
                ? 'This is a platform staff PIN, valid at every business. Only a SuperAdmin can reset it.'
                : 'You do not have permission to reset this PIN.' }}
          </p>
        } @else if (confirmPinReset) {
          <div class="note note-err pin-confirm" role="alertdialog">
            <span>
              Replace the PIN for <strong>{{ selectedPinScope?.label }}</strong>?
              {{ selectedPinScope ? scopeReach(selectedPinScope) : '' }}
              The current PIN stops working immediately.
            </span>
            <div class="pin-confirm-actions">
              <ng-container *staffCan="'Users.ResetCredentials'"><button type="button" class="act-danger act-sm" [disabled]="pinResetting" (click)="confirmResetPin()">
                {{ pinResetting ? 'Resetting…' : 'Yes, reset it' }}
              </button></ng-container>
              <button type="button" class="act-link" [disabled]="pinResetting" (click)="cancelPinReset()">Cancel</button>
            </div>
          </div>
        } @else {
          <div class="form-actions">
            <ng-container *staffCan="'Users.ResetCredentials'"><button type="button" class="act-ghost"
              [disabled]="!selectedPinScopeId || pinEmailing" (click)="emailPinSetup()">
              {{ pinEmailing ? 'Sending…' : 'Email setup link' }}
            </button></ng-container>
            <ng-container *staffCan="'Users.ResetCredentials'"><button type="button" class="act"
              [disabled]="!selectedPinScopeId || pinResetting" (click)="requestPinReset()">Reset PIN</button></ng-container>
          </div>
          <p class="hint">
            The setup link lets the user pick their own PIN, so nobody else sees it — their current
            PIN keeps working until they follow it. A reset replaces the PIN at once and shows the new
            one here a single time.
          </p>
        }
       </fieldset>
      }
    </div>

    <!-- Subscription plans (any status). These records also block user deletion. -->
    <div class="panel">
      <div class="panel-title">Subscription plans</div>

      @if (plansLoading) {
        <div class="muted">Loading plans…</div>
      } @else if (plansError) {
        <div class="note note-err">{{ plansError }}</div>
      } @else if (!userPlans.length) {
        <div class="muted">No subscription plans for this user.</div>
      } @else {
        <div class="tbl-wrap">
          <table class="tbl">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Status</th>
                <th>Billing</th>
                <th>Start</th>
                <th>End</th>
              </tr>
            </thead>
            <tbody>
              @for (plan of userPlans; track plan.userPlanId) {
                <tr>
                  <td>{{ plan.planName }}</td>
                  <td>
                    <span class="pill"
                      [class.pill-green]="planStatusClass(plan) === 'ok'"
                      [class.pill-grey]="planStatusClass(plan) === 'inactive'">
                      {{ planStatusLabel(plan) }}
                    </span>
                  </td>
                  <td>{{ plan.billingCycle || '—' }}</td>
                  <td>{{ plan.startDate ? (plan.startDate | date: 'mediumDate') : '—' }}</td>
                  <td>{{ plan.endDate ? (plan.endDate | date: 'mediumDate') : '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <p class="plans-note">Users with subscription history cannot be deleted; the records are kept for billing/audit.</p>
      }
    </div>
  </div>
} @else {
  <div class="empty">No user found for ID: {{ userId }}</div>
}
